import { ref, shallowRef } from "vue";

import api from "@/api";

/**
 * Web port of contact-mobile's tap-to-measure (`ui/screens/projects/snapshot/measure/*`), behind the
 * `capture_measure_ui` `/me` flag. Arm the Measure pill, click an object in the photo, get its size.
 * The readings, rounding, copy and error mapping deliberately match the mobile files so both clients
 * show the same answer for the same tap — change them together.
 */

export interface ObjectCropPlaneResult {
	widthMeters: number;
	heightMeters: number;
	source: string;
	planeStable?: boolean | null;
	contour?: number[][] | null;
}

export interface ObjectCropResponse {
	matched: boolean;
	contour?: number[][] | null;
	best?: ObjectCropPlaneResult | null;
	candidates?: ObjectCropPlaneResult[];
	tracking?: { scaleConfidence?: string } | null;
	surfaceWarning?: string | null;
	pipelineVersion?: number | null;
}

/** One object measured in this session. [readings] are the candidates with a usable size, best
 * first; the server's pick, `readings[0]`, is what is shown. Re-measuring the object (a click inside
 * its outline, or "Try another reading") replaces the entry in place, keeping its [id].
 * [xNormalized]/[yNormalized] are the point it was last measured from and [maskRank] which of SAM2's
 * masks at that point this reading used (0 = its usual pick); "Try another reading" re-sends the
 * same point with the next rank. */
export interface SnapshotMeasurement {
	id: number;
	xNormalized: number;
	yNormalized: number;
	response: ObjectCropResponse;
	readings: ObjectCropPlaneResult[];
	maskRank: number;
}

/** Why the last click did not add a measurement: the service is down (retry soon), the click hit
 * nothing measurable, or this photo/request failed. */
export interface SnapshotMeasureNotice {
	kind: "unavailable" | "empty" | "error";
	message: string;
}

interface MeasureTarget {
	conversationId: string;
	sourceClientId: string;
	sourceRevision: string;
}

const CENTIMETERS_PER_METER = 100;
const INCHES_PER_METER = 39.37007874015748;
const LOG_TAG = "[SnapshotMeasure]";
/** contact-api rejects a maskRank above this with 400 (captureEvidence.ts MAX_MASK_RANK); it wraps smaller ranks itself. */
const MAX_MASK_RANK = 20;

export const MEASUREMENT_UNAVAILABLE_MESSAGE = "Measurement isn't available right now. Try again in a minute.";
export const NOTHING_MEASURABLE_MESSAGE = "Nothing measurable at that point";

function hasMeasurableSize(candidate: ObjectCropPlaneResult): boolean {
	return (
		Number.isFinite(candidate.widthMeters) &&
		Number.isFinite(candidate.heightMeters) &&
		candidate.widthMeters > 0 &&
		candidate.heightMeters > 0
	);
}

function sameReading(first: ObjectCropPlaneResult, second: ObjectCropPlaneResult): boolean {
	return JSON.stringify(first) === JSON.stringify(second);
}

/** Mobile `measurableReadings`: nothing for an unmatched tap (a `plane_fallback` there is the whole
 * surface, not the tapped object), size-less candidates dropped, and the server's `best` first. */
export function measurableReadings(response: ObjectCropResponse): ObjectCropPlaneResult[] {
	if (!response.matched) return [];
	const best = response.best && hasMeasurableSize(response.best) ? response.best : null;
	const measurable = (response.candidates ?? []).filter(hasMeasurableSize);
	if (measurable.length === 0) return best ? [best] : [];
	const bestIndex = best ? measurable.findIndex((candidate) => sameReading(candidate, best)) : -1;
	if (bestIndex <= 0) return measurable;
	return [measurable[bestIndex], ...measurable.filter((_, index) => index !== bestIndex)];
}

function formatOneDecimal(meters: number, unitsPerMeter: number): string | null {
	if (!Number.isFinite(meters) || meters <= 0) return null;
	const tenths = Math.round(meters * unitsPerMeter * 10);
	if (tenths <= 0) return null;
	return `${Math.floor(tenths / 10)}.${tenths % 10}`;
}

/** `55.0 × 75.0 cm (21.7 × 29.5 in)` — both units, as on mobile; null when either axis has no size. */
export function formatMeasurementSize(candidate: ObjectCropPlaneResult): string | null {
	const widthCm = formatOneDecimal(candidate.widthMeters, CENTIMETERS_PER_METER);
	const heightCm = formatOneDecimal(candidate.heightMeters, CENTIMETERS_PER_METER);
	const widthIn = formatOneDecimal(candidate.widthMeters, INCHES_PER_METER);
	const heightIn = formatOneDecimal(candidate.heightMeters, INCHES_PER_METER);
	if (!widthCm || !heightCm || !widthIn || !heightIn) return null;
	return `${widthCm} × ${heightCm} cm (${widthIn} × ${heightIn} in)`;
}

export function measurementWarning(response: ObjectCropResponse, candidate: ObjectCropPlaneResult | null): string | null {
	if (response.surfaceWarning === "raised_object_on_largest_plane")
		return "No surface was found under this object, so it was measured against the floor — it may read too large.";
	if (response.surfaceWarning) return "This reading may be measured against the wrong surface.";
	if (candidate?.source === "plane_fallback" && candidate.planeStable === false)
		return "The surface under this object hadn't settled yet — take another photo, moving the phone a little first.";
	return null;
}

/** Mobile `failureStateFor`: 503 is the service saying "come back in a moment", everything else is
 * about this photo or this request. `_handleResponse` rejects with `{ httpStatus }` for non-2xx. */
function noticeFor(error: unknown): SnapshotMeasureNotice {
	const httpStatus = (error as { httpStatus?: number } | null)?.httpStatus;
	if (httpStatus === 503) return { kind: "unavailable", message: MEASUREMENT_UNAVAILABLE_MESSAGE };
	if (httpStatus === 404) return { kind: "error", message: "This photo isn't ready to measure yet. Try again in a moment." };
	if (httpStatus === 401) return { kind: "error", message: "Your session expired. Sign in again to measure." };
	if (httpStatus === 429) return { kind: "error", message: "That's a lot of measuring! Wait a moment and try again." };
	if (httpStatus && httpStatus >= 400 && httpStatus < 500) return { kind: "error", message: "This photo can't be measured." };
	if (httpStatus && httpStatus >= 500) return { kind: "error", message: "Measuring failed. Try again." };
	return { kind: "error", message: "Couldn't measure — check your connection and try again." };
}

/** The outline to draw for a measurement: the chosen reading's own contour, else the mask's. */
export function measurementContour(measurement: SnapshotMeasurement): number[][] | null {
	return measurement.readings[0]?.contour ?? measurement.response.contour ?? null;
}

/** Even-odd ray cast; the contour is normalized 0-1 on both axes, which containment does not mind. */
export function contourContains(contour: readonly (readonly number[])[] | null | undefined, x: number, y: number): boolean {
	if (!contour || contour.length < 3) return false;
	let inside = false;
	for (let index = 0, previous = contour.length - 1; index < contour.length; previous = index++) {
		const [currentX, currentY] = contour[index];
		const [previousX, previousY] = contour[previous];
		if (currentY > y !== previousY > y && x < ((previousX - currentX) * (y - currentY)) / (previousY - currentY) + currentX)
			inside = !inside;
	}
	return inside;
}

/**
 * One instance per lightbox. The lightbox calls [load] for the photo on screen; the Measure page adds
 * one [SnapshotMeasurement] per click to a session-only list that lives until the photo changes or
 * the lightbox closes. Every async answer is dropped if the photo changed while it was in flight, so
 * a late response can never land in another photo's list.
 */
export function useSnapshotMeasure() {
	const target = shallowRef<MeasureTarget | null>(null);
	const measurements = shallowRef<SnapshotMeasurement[]>([]);
	const pendingTap = shallowRef<{ xNormalized: number; yNormalized: number; targetId: number | null } | null>(null);
	const notice = shallowRef<SnapshotMeasureNotice | null>(null);
	const selectedId = ref<number | null>(null);
	let generation = 0;
	let nextId = 1;

	/** Resolves whether [snapshotId] has capture evidence to measure against. No evidence (404, a
	 * legacy or non-native photo) means no Measure button at all, exactly like mobile's pill. */
	async function load(snapshotId: string | null | undefined, conversationId: string | null | undefined): Promise<void> {
		reset();
		const loadGeneration = generation;
		if (!snapshotId || !conversationId) return;
		try {
			const summary = (await api.getCaptureEvidence("snapshot", snapshotId)) as { sourceClientId?: string; sourceRevision?: string };
			if (loadGeneration !== generation) return;
			if (!summary?.sourceClientId) {
				console.warn(`${LOG_TAG} evidence summary has no sourceClientId; measure hidden`, { snapshotId, conversationId });
				return;
			}
			target.value = { conversationId, sourceClientId: summary.sourceClientId, sourceRevision: summary.sourceRevision ?? "1" };
		} catch (error) {
			if (loadGeneration !== generation) return;
			const httpStatus = (error as { httpStatus?: number } | null)?.httpStatus;
			if (httpStatus !== 404)
				console.warn(`${LOG_TAG} could not load capture evidence; measure hidden`, { snapshotId, conversationId, httpStatus });
		}
	}

	/** A click on the photo. Inside an object already measured, it re-measures THAT object from the
	 * clicked point (replacing its entry); anywhere else it adds a new measurement. */
	function tap(xNormalized: number, yNormalized: number): Promise<void> {
		const x = Math.min(1, Math.max(0, xNormalized));
		const y = Math.min(1, Math.max(0, yNormalized));
		const hit = [...measurements.value].reverse().find((measurement) => contourContains(measurementContour(measurement), x, y));
		return measureAt(x, y, hit?.id ?? null, 0);
	}

	/** "Try another reading": re-measure the object from the SAME point it was last measured from,
	 * asking the server for SAM2's next mask there (the same point alone would return the cached answer). */
	function remeasure(id: number): Promise<void> {
		const measurement = measurements.value.find((candidate) => candidate.id === id);
		if (!measurement) {
			console.warn(`${LOG_TAG} remeasure ignored, measurement is gone`, { id });
			return Promise.resolve();
		}
		return measureAt(measurement.xNormalized, measurement.yNormalized, id, (measurement.maskRank + 1) % (MAX_MASK_RANK + 1));
	}

	async function measureAt(x: number, y: number, targetId: number | null, maskRank: number): Promise<void> {
		const current = target.value;
		if (!current) {
			console.warn(`${LOG_TAG} tap ignored, no measurable photo`, { xNormalized: x, yNormalized: y });
			return;
		}
		if (pendingTap.value) {
			console.warn(`${LOG_TAG} tap ignored, a measurement is already in flight`, {
				...current,
				xNormalized: x,
				yNormalized: y,
				targetId
			});
			return;
		}
		const tapGeneration = generation;
		pendingTap.value = { xNormalized: x, yNormalized: y, targetId };
		notice.value = null;
		if (targetId !== null) selectedId.value = targetId;
		try {
			const response = (await api.cropCaptureObjectAtPoint(
				current.conversationId,
				current.sourceClientId,
				current.sourceRevision,
				x,
				y,
				maskRank
			)) as ObjectCropResponse;
			if (tapGeneration !== generation) return;
			const readings = measurableReadings(response);
			if (readings.length === 0) {
				console.warn(`${LOG_TAG} tap produced no usable reading`, {
					...current,
					targetId,
					maskRank,
					matched: response.matched,
					candidates: response.candidates?.length ?? 0,
					hasBest: !!response.best,
					pipelineVersion: response.pipelineVersion
				});
				notice.value = { kind: "empty", message: NOTHING_MEASURABLE_MESSAGE };
				return;
			}
			const previous = targetId === null ? undefined : measurements.value.find((candidate) => candidate.id === targetId);
			const measurement: SnapshotMeasurement = {
				id: previous?.id ?? nextId++,
				xNormalized: x,
				yNormalized: y,
				response,
				readings,
				maskRank
			};
			measurements.value = previous
				? measurements.value.map((candidate) => (candidate.id === previous.id ? measurement : candidate))
				: [...measurements.value, measurement];
			selectedId.value = measurement.id;
		} catch (error) {
			if (tapGeneration !== generation) return;
			console.warn(`${LOG_TAG} measurement failed`, { ...current, targetId, error });
			notice.value = noticeFor(error);
		} finally {
			if (tapGeneration === generation) pendingTap.value = null;
		}
	}

	function updateMeasurement(id: number, change: (measurement: SnapshotMeasurement) => SnapshotMeasurement | null): void {
		measurements.value = measurements.value.flatMap((measurement) => {
			if (measurement.id !== id) return [measurement];
			const changed = change(measurement);
			return changed ? [changed] : [];
		});
	}

	function remove(id: number): void {
		updateMeasurement(id, () => null);
		if (selectedId.value === id) selectedId.value = measurements.value.at(-1)?.id ?? null;
	}

	function select(id: number | null): void {
		selectedId.value = id;
	}

	function dismissNotice(): void {
		notice.value = null;
	}

	/** Forgets the photo and its session list; an in-flight measurement's answer is dropped. */
	function reset(): void {
		generation++;
		target.value = null;
		measurements.value = [];
		pendingTap.value = null;
		notice.value = null;
		selectedId.value = null;
	}

	// Returned as plain refs: callers read them; every change goes through the functions below.
	return {
		target,
		measurements,
		pendingTap,
		notice,
		selectedId,
		load,
		tap,
		remeasure,
		remove,
		select,
		dismissNotice,
		reset
	};
}

export type SnapshotMeasure = ReturnType<typeof useSnapshotMeasure>;
